import { useEffect, useState } from 'react';
import {
  ESTADOS_EQUIPO,
  ESTADOS_RECEPCION,
  MODALIDADES_STAGING,
  TIPOS_EQUIPO,
} from '@scm/contratos';
import { api } from '../lib/api.js';
import { entero, fecha } from '../lib/formato.js';
import {
  CLS_EQP,
  CLS_RECEPCION,
  COLOR_EQP,
  txt,
  TXT_EQP,
  TXT_MODALIDAD,
  TXT_RECEPCION,
  TXT_TKT,
} from '../lib/enums.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { columnasPresentes, ConmutadorVista, Kanban, useVista } from '../piezas/Kanban.jsx';
import { Cargando, Error as AvisoError, Toast, Vacio } from '../piezas/Estados.jsx';
import { Formulario, type Campo } from '../piezas/Formulario.jsx';
import { Tabla, type Columna } from '../piezas/Tabla.jsx';
import { useSesion } from '../piezas/Sesion.jsx';
import { opciones, type Opcion } from '../lib/opciones.js';

/**
 * Second touch.
 *
 * El trabajo ocurre POR LOTE. Estas pantallas estan hechas para eso: la
 * captura de series acepta una lista pegada, la asignacion de sitio toma los
 * equipos marcados, y el embarque agrupa por sitio. Una pantalla que obligue a
 * tocar los equipos de uno en uno no se usa con un lote de 500.
 */

/* ------------------------------- Tablero --------------------------------- */

interface Tablero {
  equipos_por_estado: { estado: string; n: number }[];
  recepciones: { abiertas: number; con_diferencia: number; total: number };
  remesas: { en_curso: number; validadas: number; cerradas: number };
  por_reclamar: { equipos: number };
  sin_sitio: { equipos: number };
}

export function TableroStaging(): JSX.Element {
  const [datos, setDatos] = useState<Tablero | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [proyecto, setProyecto] = useState('');
  const [proyectos, setProyectos] = useState<Opcion[]>([]);

  useEffect(() => {
    opciones('proyectos').then(setProyectos).catch(setError);
  }, []);

  useEffect(() => {
    setDatos(null);
    api
      .obtener<Tablero>(`/v1/staging/tablero${proyecto ? `?proyecto_id=${proyecto}` : ''}`)
      .then(({ datos: d }) => setDatos(d))
      .catch(setError);
  }, [proyecto]);

  const total = datos?.equipos_por_estado.reduce((a, x) => a + x.n, 0) ?? 0;

  return (
    <>
      <Pagehead />

      <div className="fila wrap" style={{ marginBottom: 12 }}>
        <label className="campo" style={{ maxWidth: 420 }}>
          <span>Proyecto</span>
          <select
            value={proyecto}
            onChange={(ev) => setProyecto(ev.target.value)}
            data-prueba="filtro-proyecto"
          >
            <option value="">Todos los proyectos</option>
            {proyectos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error !== null && <AvisoError error={error} />}

      {datos === null ? (
        <Cargando que="el tablero" />
      ) : (
        <>
          <div className="grid kpis" data-prueba="kpis-staging">
            <Kpi titulo="Equipos en el padron" valor={total} icono="🖥" />
            <Kpi
              titulo="Recepciones con diferencia"
              valor={datos.recepciones.con_diferencia}
              icono="📦"
              ayuda="Se reclaman al proveedor"
              alarma={datos.recepciones.con_diferencia > 0}
            />
            <Kpi titulo="Remesas en curso" valor={datos.remesas.en_curso} icono="💽" />
            <Kpi
              titulo="Por reclamar al proveedor"
              valor={datos.por_reclamar.equipos}
              icono="⚠"
              ayuda="Equipos con error de carga"
              alarma={datos.por_reclamar.equipos > 0}
            />
            <Kpi
              titulo="Listos sin destino"
              valor={datos.sin_sitio.equipos}
              icono="📍"
              ayuda="No se pueden embarcar hasta asignarles sitio"
              alarma={datos.sin_sitio.equipos > 0}
            />
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Como va el proceso</h3>
            {datos.equipos_por_estado.length === 0 ? (
              <Vacio
                mensaje="Todavia no hay equipos en el padron"
                ayuda="Nacen al dar de alta una recepcion de pallets."
                icono="🖥"
              />
            ) : (
              /* Barra apilada y no una grafica de pastel: lo que importa es
                 cuanto falta para que todo llegue a instalado, y eso se lee
                 en una linea que avanza, no en rebanadas. */
              <>
                <div
                  style={{
                    display: 'flex',
                    height: 28,
                    borderRadius: 6,
                    overflow: 'hidden',
                    /* 2px de separacion entre segmentos, con el color de la
                       superficie: sin eso dos estados contiguos de tono
                       parecido se leen como uno solo. */
                    gap: 2,
                  }}
                  data-prueba="barra-estados"
                >
                  {datos.equipos_por_estado.map((e) => (
                    <div
                      key={e.estado}
                      title={`${txt(TXT_EQP, e.estado)}: ${entero(e.n)}`}
                      style={{
                        width: `${(e.n / total) * 100}%`,
                        background: COLOR_EQP[e.estado] ?? '#8b94a7',
                      }}
                    />
                  ))}
                </div>
                <div className="fila wrap" style={{ marginTop: 10, gap: 12 }}>
                  {datos.equipos_por_estado.map((e) => (
                    <span key={e.estado} className="fila" style={{ gap: 6, fontSize: 12 }}>
                      <span
                        aria-hidden="true"
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 3,
                          background: COLOR_EQP[e.estado] ?? '#8b94a7',
                          display: 'inline-block',
                        }}
                      />
                      {txt(TXT_EQP, e.estado)}
                      <b>{entero(e.n)}</b>
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

function Kpi({
  titulo,
  valor,
  icono,
  ayuda,
  alarma = false,
}: {
  titulo: string;
  valor: number;
  icono: string;
  ayuda?: string;
  alarma?: boolean;
}): JSX.Element {
  return (
    <div className="card kpi">
      <span className="ico" aria-hidden="true">
        {icono}
      </span>
      <span className="val">
        <span>{titulo}</span>
        <b style={alarma && valor > 0 ? { color: 'var(--bad-ac)' } : undefined}>{entero(valor)}</b>
        {ayuda !== undefined && (
          <small className="muted" style={{ fontSize: 11 }}>
            {ayuda}
          </small>
        )}
      </span>
    </div>
  );
}

/* ----------------------------- Recepciones ------------------------------- */

interface RecepcionFila extends Record<string, unknown> {
  id: string;
  folio: string;
  cliente: string;
  proyecto: string;
  proyecto_nombre: string;
  modalidad: string;
  facilidad: string | null;
  recibido_en: string;
  recibido_por: string;
  pallets: number;
  cantidad_declarada: number;
  cantidad_contada: number | null;
  diferencia: number | null;
  estado: string;
  equipos: number;
}

export function Recepciones(): JSX.Element {
  const [vista, ponerVista] = useVista('recepciones');
  const [filas, setFilas] = useState<RecepcionFila[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [alta, setAlta] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const { puede } = useSesion();

  useEffect(() => {
    setFilas(null);
    api
      .obtener<RecepcionFila[]>('/v1/recepciones')
      .then(({ datos }) => setFilas(datos))
      .catch(setError);
  }, [recarga]);

  return (
    <>
      <Pagehead />

      <div className="fila wrap" style={{ marginBottom: 12 }}>
        <ConmutadorVista vista={vista} poner={ponerVista} />
        <span className="crece" />
        {puede('MOD_STAGING', 'escribir') && (
          <button
            type="button"
            className="btn pri"
            onClick={() => setAlta(true)}
            data-prueba="nueva-recepcion"
          >
            + Recibir pallets
          </button>
        )}
      </div>

      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {filas === null ? (
          <Cargando que="las recepciones" />
        ) : filas.length === 0 ? (
          <Vacio
            mensaje="Todavia no hay recepciones"
            ayuda="De una recepcion nacen los equipos del padron, con su serie y su ticket."
            icono="📦"
          />
        ) : vista === 'kanban' ? (
          <Kanban
            items={filas}
            estadoDe={(r) => String(r.estado)}
            claveDe={(r) => String(r.id)}
            columnas={columnasPresentes(
              filas.map((r) => String(r.estado)),
              [{ estados: ESTADOS_RECEPCION }],
            )}
            etiquetas={TXT_RECEPCION}
            clases={CLS_RECEPCION}
            tarjeta={(r) => (
              <>
                <div className="folio">{String(r.folio)}</div>
                <b>{String(r.proyecto_nombre ?? r.proyecto)}</b>
                <div className="kan-pie">
                  <span className="muted">{entero(Number(r.pallets))} pallets</span>
                  <span className="muted">{entero(Number(r.equipos))} equipos</span>
                </div>
              </>
            )}
          />
        ) : (
          <table data-prueba="tabla-recepciones">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Proyecto</th>
                <th>Modalidad</th>
                <th>Recibido</th>
                <th className="num">Pallets</th>
                <th className="num">Declarado</th>
                <th className="num">Contado</th>
                <th className="num">Equipos</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => (
                <tr key={r.id}>
                  <td className="folio">{r.folio}</td>
                  <td>
                    {r.proyecto}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {r.proyecto_nombre}
                    </div>
                  </td>
                  <td>
                    {txt(TXT_MODALIDAD, r.modalidad)}
                    {r.facilidad !== null && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {r.facilidad}
                      </div>
                    )}
                  </td>
                  <td>
                    {fecha(r.recibido_en)}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {r.recibido_por}
                    </div>
                  </td>
                  <td className="num">{entero(r.pallets)}</td>
                  <td className="num">{entero(r.cantidad_declarada)}</td>
                  <td className="num">
                    {r.cantidad_contada === null ? '—' : entero(r.cantidad_contada)}
                    {r.diferencia !== null && r.diferencia !== 0 && (
                      /* Pastilla y no texto rojo de 11px: axe lo marco con
                         contraste insuficiente, y con razon. La pastilla ya
                         cumple y es como el resto de la plataforma marca lo
                         que esta mal. */
                      <div>
                        <span className="tag t-hot">
                          {r.diferencia > 0 ? `+${r.diferencia}` : r.diferencia}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="num">{entero(r.equipos)}</td>
                  <td>
                    <span className={`tag ${CLS_RECEPCION[r.estado] ?? 't-mute'}`}>
                      {txt(TXT_RECEPCION, r.estado)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {alta && (
        <AltaRecepcion
          alCerrar={() => setAlta(false)}
          alGuardar={(m) => {
            setAlta(false);
            setAviso(m);
            setRecarga((n) => n + 1);
          }}
        />
      )}
      {aviso !== null && <Toast mensaje={aviso} />}
    </>
  );
}

interface SerieCapturada {
  serie: string;
  marca: string;
  modelo: string;
  tipo_equipo: string;
}

/**
 * Alta de recepcion con captura MASIVA de series.
 *
 * El campo grande acepta una lista pegada: una serie por renglon, o con marca
 * y modelo separados por coma o tabulador, que es como sale de un Excel. Un
 * lote son cientos de series y un formulario de una en una no se usa: se
 * termina capturando en otra parte y subiendo un archivo por correo.
 */
function AltaRecepcion({
  alCerrar,
  alGuardar,
}: {
  alCerrar: () => void;
  alGuardar: (mensaje: string) => void;
}): JSX.Element {
  const [proyectos, setProyectos] = useState<Opcion[]>([]);
  const [almacenes, setAlmacenes] = useState<Opcion[]>([]);
  const [proyecto, setProyecto] = useState('');
  const [modalidad, setModalidad] = useState<string>('STG_FACILIDAD');
  const [facilidad, setFacilidad] = useState('');
  const [recibidoEn, setRecibidoEn] = useState(new Date().toISOString().slice(0, 10));
  const [recibidoPor, setRecibidoPor] = useState('');
  const [pallets, setPallets] = useState('1');
  const [declarada, setDeclarada] = useState('');
  const [contada, setContada] = useState('');
  const [marcaOmision, setMarcaOmision] = useState('');
  const [modeloOmision, setModeloOmision] = useState('');
  const [tipoOmision, setTipoOmision] = useState<string>('Mini');
  const [pegado, setPegado] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    opciones('proyectos').then(setProyectos).catch(setError);
    opciones('almacenes').then(setAlmacenes).catch(setError);
  }, []);

  const series = analizarSeries(pegado, marcaOmision, modeloOmision, tipoOmision);
  const repetidas = seriesRepetidas(series);

  async function guardar(): Promise<void> {
    setGuardando(true);
    setError(null);
    try {
      const { datos } = await api.enviar<{ folio: string; estado: string; equipos: number }>(
        '/v1/recepciones',
        {
          proyecto_id: proyecto,
          modalidad,
          ...(modalidad === 'STG_FACILIDAD' ? { almacen_id: facilidad } : {}),
          recibido_en: recibidoEn,
          recibido_por: recibidoPor,
          pallets: Number(pallets),
          cantidad_declarada: Number(declarada || series.length),
          ...(contada === '' ? {} : { cantidad_contada: Number(contada) }),
          series,
        },
      );
      alGuardar(`${datos.folio}: nacieron ${entero(datos.equipos)} equipos virgenes.`);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  const listo =
    proyecto !== '' &&
    recibidoPor.trim().length >= 3 &&
    series.length > 0 &&
    repetidas.length === 0 &&
    (modalidad !== 'STG_FACILIDAD' || facilidad !== '');

  return (
    <div className="panel-lado" role="dialog" aria-modal="true" data-prueba="panel-recepcion">
      <div className="panel-h">
        <h3>Recibir pallets</h3>
        <button type="button" className="btn sm" onClick={alCerrar}>
          Cerrar
        </button>
      </div>

      <div className="panel-b">
        {error !== null && <AvisoError error={error} />}

        <div className="grid campos">
          <label className="campo">
            <span>
              Proyecto <b>*</b>
            </span>
            <select value={proyecto} onChange={(ev) => setProyecto(ev.target.value)}>
              <option value="">Elige…</option>
              {proyectos
                .filter((p) => p.activo)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.etiqueta}
                  </option>
                ))}
            </select>
          </label>

          <label className="campo">
            <span>
              Modalidad <b>*</b>
            </span>
            <select value={modalidad} onChange={(ev) => setModalidad(ev.target.value)}>
              {MODALIDADES_STAGING.map((m) => (
                <option key={m} value={m}>
                  {txt(TXT_MODALIDAD, m)}
                </option>
              ))}
            </select>
            <small className="muted">
              En sitio destino no hay embarque: el equipo ya esta donde se queda.
            </small>
          </label>

          {modalidad === 'STG_FACILIDAD' && (
            <label className="campo">
              <span>
                Facilidad <b>*</b>
              </span>
              <select value={facilidad} onChange={(ev) => setFacilidad(ev.target.value)}>
                <option value="">Elige…</option>
                {almacenes
                  .filter((a) => a.activo)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.etiqueta}
                    </option>
                  ))}
              </select>
              <small className="muted">Un almacen de tipo facilidad de staging.</small>
            </label>
          )}

          <label className="campo">
            <span>Fecha de recepcion</span>
            <input
              type="date"
              value={recibidoEn}
              onChange={(ev) => setRecibidoEn(ev.target.value)}
            />
          </label>

          <label className="campo">
            <span>
              Recibio <b>*</b>
            </span>
            <input
              value={recibidoPor}
              onChange={(ev) => setRecibidoPor(ev.target.value)}
              placeholder="Nombre completo"
            />
          </label>

          <label className="campo">
            <span>Pallets</span>
            <input
              type="number"
              min={1}
              value={pallets}
              onChange={(ev) => setPallets(ev.target.value)}
            />
          </label>

          <label className="campo">
            <span>Cantidad declarada</span>
            <input
              type="number"
              min={1}
              value={declarada}
              onChange={(ev) => setDeclarada(ev.target.value)}
              placeholder={String(series.length)}
            />
            <small className="muted">Lo que dice la lista de empaque.</small>
          </label>

          <label className="campo">
            <span>Cantidad contada</span>
            <input
              type="number"
              min={0}
              value={contada}
              onChange={(ev) => setContada(ev.target.value)}
              placeholder="Al terminar el conteo"
            />
            <small className="muted">
              Si no coincide con lo declarado, la recepcion queda con diferencia y eso se reclama al
              proveedor.
            </small>
          </label>
        </div>

        <h4>Series</h4>
        <div className="grid campos">
          <label className="campo">
            <span>Marca por omision</span>
            <input value={marcaOmision} onChange={(ev) => setMarcaOmision(ev.target.value)} />
          </label>
          <label className="campo">
            <span>Modelo por omision</span>
            <input value={modeloOmision} onChange={(ev) => setModeloOmision(ev.target.value)} />
          </label>
          <label className="campo">
            <span>Tipo por omision</span>
            <select value={tipoOmision} onChange={(ev) => setTipoOmision(ev.target.value)}>
              {TIPOS_EQUIPO.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="campo">
          <span>Pega la lista, o lee los codigos de barras uno tras otro</span>
          <textarea
            rows={8}
            value={pegado}
            onChange={(ev) => setPegado(ev.target.value)}
            data-prueba="series"
            placeholder={'SN-0001\nSN-0002, Lenovo, ThinkCentre M70q\nSN-0003\tDell\tOptiPlex 7010'}
          />
          <small className="muted">
            Una por renglon. Si el renglon trae marca y modelo separados por coma o tabulador, se
            usan esos; si no, los de arriba.
          </small>
        </label>

        <div className="fila wrap" style={{ gap: 12 }}>
          <span data-prueba="cuenta-series">
            <b>{entero(series.length)}</b> series leidas
          </span>
          {repetidas.length > 0 && (
            <span style={{ color: 'var(--bad-ac)' }} data-prueba="series-repetidas">
              {repetidas.length} repetidas en la lista: {repetidas.slice(0, 5).join(', ')}
            </span>
          )}
          {declarada !== '' && series.length !== Number(declarada) && (
            <span className="muted">
              Ojo: capturaste {series.length} y declaraste {declarada}.
            </span>
          )}
        </div>
      </div>

      <div className="panel-f">
        <button type="button" className="btn ghost" onClick={alCerrar}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn pri"
          disabled={!listo || guardando}
          onClick={() => void guardar()}
          data-prueba="guardar-recepcion"
        >
          {guardando ? 'Guardando…' : `Dar de alta ${entero(series.length)} equipos`}
        </button>
      </div>
    </div>
  );
}

/**
 * Lee la lista pegada.
 *
 * Acepta los tres formatos en que de verdad llega: una serie por renglon, o
 * serie con marca y modelo separados por coma o por tabulador, que es lo que
 * sale al copiar de un Excel. Los renglones vacios se ignoran en silencio
 * porque una lista pegada siempre trae alguno al final.
 */
function analizarSeries(
  pegado: string,
  marca: string,
  modelo: string,
  tipo: string,
): SerieCapturada[] {
  const salida: SerieCapturada[] = [];
  for (const renglon of pegado.split(/\r?\n/)) {
    const partes = renglon
      .split(/[\t,;]/)
      .map((p) => p.trim())
      .filter((p) => p !== '');
    if (partes.length === 0) continue;
    const serie = partes[0];
    if (serie === undefined || serie === '') continue;
    salida.push({
      serie,
      marca: partes[1] ?? marca,
      modelo: partes[2] ?? modelo,
      tipo_equipo: partes[3] ?? tipo,
    });
  }
  return salida;
}

/** Las repetidas DENTRO de la lista, antes de mandarla. */
function seriesRepetidas(series: SerieCapturada[]): string[] {
  const vistas = new Set<string>();
  const repetidas = new Set<string>();
  for (const s of series) {
    const k = s.serie.toUpperCase();
    if (vistas.has(k)) repetidas.add(s.serie);
    vistas.add(k);
  }
  return [...repetidas];
}

/* ------------------------------- Padron ---------------------------------- */

interface EquipoFila extends Record<string, unknown> {
  id: string;
  serie: string;
  marca: string;
  modelo: string;
  tipo_equipo: string;
  estado: string;
  proyecto: string;
  facilidad: string | null;
  sitio: string | null;
  sitio_nombre: string | null;
  area_destino: string | null;
  imagen: string | null;
  id_imagen: string | null;
  remesa: string | null;
  etiquetado_en: string | null;
  guia: string | null;
  ticket: string | null;
  ticket_estado: string | null;
}

/**
 * Lo que se le edita a un equipo.
 *
 * La SERIE no esta: es su identidad, y una identidad que se edita deja de
 * identificar. El estado tampoco: lo mueven la remesa y el embarque, no la
 * captura. El servidor rechaza los dos de todos modos, porque no estan entre
 * los escribibles de la entidad.
 */
const CAMPOS_EQUIPO: Campo[] = [
  {
    nombre: 'sitio_id',
    etiqueta: 'Sitio destino',
    tipo: 'catalogo',
    catalogo: 'sitios',
    ayuda: 'Vacio mientras el equipo no tenga destino. Solo sitios del mismo cliente.',
  },
  {
    nombre: 'area_destino',
    etiqueta: 'Area destino',
    tipo: 'texto',
    ayuda: 'Va impresa en la etiqueta de activo fijo.',
  },
  {
    nombre: 'nomenclatura',
    etiqueta: 'Nombre en la red',
    tipo: 'texto',
    ayuda: 'Cuando ya se sabe como se va a llamar en la red del cliente.',
  },
  { nombre: 'almacen_id', etiqueta: 'Facilidad', tipo: 'catalogo', catalogo: 'almacenes' },
  { nombre: 'nota', etiqueta: 'Nota', tipo: 'texto-largo', ancho: true },
  { nombre: 'activo', etiqueta: 'Activo', tipo: 'si-no' },
];

export function Padron(): JSX.Element {
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [recarga, setRecarga] = useState(0);
  const [aviso, setAviso] = useState<string | null>(null);
  const [asignando, setAsignando] = useState(false);
  const [editando, setEditando] = useState<EquipoFila | null>(null);
  const { puede } = useSesion();

  const columnas: Columna<EquipoFila>[] = [
    {
      campo: 'serie',
      titulo: 'Serie',
      celda: (f) => (
        <>
          <label className="fila" style={{ gap: 8 }} data-prueba={`marca-${f.serie}`}>
            {puede('MOD_STAGING', 'escribir') && (
              <input
                type="checkbox"
                checked={marcados.has(f.id)}
                onChange={(ev) => {
                  const copia = new Set(marcados);
                  if (ev.target.checked) copia.add(f.id);
                  else copia.delete(f.id);
                  setMarcados(copia);
                }}
              />
            )}
            <span className="folio">{f.serie}</span>
          </label>
          <div className="muted" style={{ fontSize: 11 }}>
            {f.marca} {f.modelo} · {f.tipo_equipo}
          </div>
        </>
      ),
    },
    {
      campo: 'estado',
      titulo: 'Estado',
      celda: (f) => (
        <>
          <span className={`tag ${CLS_EQP[f.estado] ?? 't-mute'}`}>{txt(TXT_EQP, f.estado)}</span>
          {f.ticket_estado !== null && (
            <div className="muted" style={{ fontSize: 11 }}>
              {txt(TXT_TKT, f.ticket_estado)}
            </div>
          )}
        </>
      ),
    },
    {
      campo: 'imagen',
      titulo: 'Imagen',
      celda: (f) =>
        f.imagen === null ? (
          <span className="muted">Sin imagen</span>
        ) : (
          <>
            {f.imagen}
            <div className="muted" style={{ fontSize: 11 }}>
              {f.id_imagen}
            </div>
          </>
        ),
    },
    {
      campo: 'sitio',
      titulo: 'Destino',
      celda: (f) =>
        f.sitio === null ? (
          /* Lo que detiene el embarque, dicho donde se ve. */
          <span className="tag t-warm">Sin destino</span>
        ) : (
          <>
            <span className="folio">{f.sitio}</span>
            <div className="muted" style={{ fontSize: 11 }}>
              {f.sitio_nombre}
              {f.area_destino !== null && ` · ${f.area_destino}`}
            </div>
          </>
        ),
    },
    { campo: 'remesa', titulo: 'Remesa', secundaria: true },
    { campo: 'guia', titulo: 'Guia', secundaria: true },
    {
      campo: 'id',
      titulo: '',
      /* Boton explicito en vez de fila presionable: con la casilla de marcar
         adentro, una fila que ademas es un boton anida controles —axe lo
         marco en los 26 renglones— y pelea el clic de marcar con el de
         abrir. */
      celda: (f) => (
        <button type="button" className="btn sm ghost" onClick={() => setEditando(f)}>
          Editar
        </button>
      ),
    },
  ];

  return (
    <>
      <Pagehead />

      {marcados.size > 0 && (
        <div className="card fila wrap" style={{ marginBottom: 12, gap: 12 }}>
          <b data-prueba="marcados">{entero(marcados.size)} equipos marcados</b>
          <span className="crece" />
          <button type="button" className="btn sm" onClick={() => setMarcados(new Set())}>
            Quitar la marca
          </button>
          <button
            type="button"
            className="btn pri sm"
            onClick={() => setAsignando(true)}
            data-prueba="asignar-sitio"
          >
            Asignar sitio destino
          </button>
        </div>
      )}

      <Tabla<EquipoFila>
        kanban={{
          pantalla: 'equipos',
          estadoDe: (e) => String(e.estado),
          flujos: [{ estados: ESTADOS_EQUIPO }],
          etiquetas: TXT_EQP,
          clases: CLS_EQP,
          tarjeta: (e) => (
            <>
              <div className="folio">{String(e.serie)}</div>
              <b>
                {String(e.marca)} {String(e.modelo)}
              </b>
              <div className="kan-pie">
                <span className="muted">{String(e.sitio ?? 'sin sitio')}</span>
                <span className="muted">{String(e.proyecto ?? '')}</span>
              </div>
            </>
          ),
        }}
        ruta="equipos"
        columnas={columnas}
        que="los equipos"
        icono="🖥"
        buscable
        csv
        recarga={recarga}
        llave={(f) => f.id}
        filtro={{
          parametro: 'estado',
          valores: [
            { valor: '', etiqueta: 'Todos' },
            { valor: 'EQP_VIRGEN', etiqueta: 'Virgenes' },
            { valor: 'EQP_EN_PROCESO', etiqueta: 'En carga' },
            { valor: 'EQP_LISTO', etiqueta: 'Listos' },
            { valor: 'EQP_EMBARCADO', etiqueta: 'Embarcados' },
            { valor: 'EQP_INSTALADO', etiqueta: 'Instalados' },
            { valor: 'EQP_ERROR', etiqueta: 'Con error' },
          ],
        }}
      />

      {editando !== null && (
        <Formulario
          ruta="equipos"
          campos={CAMPOS_EQUIPO}
          titulo={`Equipo ${editando.serie}`}
          actual={editando}
          llave={editando.id}
          alCerrar={() => setEditando(null)}
          alGuardar={() => {
            setEditando(null);
            setRecarga((n) => n + 1);
          }}
        />
      )}

      {asignando && (
        <AsignarSitio
          equipos={[...marcados]}
          alCerrar={() => setAsignando(false)}
          alGuardar={(m) => {
            setAsignando(false);
            setMarcados(new Set());
            setAviso(m);
            setRecarga((n) => n + 1);
          }}
        />
      )}
      {aviso !== null && <Toast mensaje={aviso} />}
    </>
  );
}

function AsignarSitio({
  equipos,
  alCerrar,
  alGuardar,
}: {
  equipos: string[];
  alCerrar: () => void;
  alGuardar: (mensaje: string) => void;
}): JSX.Element {
  const [sitios, setSitios] = useState<Opcion[]>([]);
  const [sitio, setSitio] = useState('');
  const [area, setArea] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    opciones('sitios').then(setSitios).catch(setError);
  }, []);

  async function guardar(): Promise<void> {
    setGuardando(true);
    setError(null);
    try {
      const { datos } = await api.enviar<{ equipos: number }>('/v1/equipos/asignar-sitio', {
        equipos,
        sitio_id: sitio,
        ...(area.trim() === '' ? {} : { area_destino: area.trim() }),
      });
      alGuardar(`${entero(datos.equipos)} equipos con destino asignado.`);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="panel-lado" role="dialog" aria-modal="true" data-prueba="panel-asignar">
      <div className="panel-h">
        <h3>Sitio destino de {entero(equipos.length)} equipos</h3>
        <button type="button" className="btn sm" onClick={alCerrar}>
          Cerrar
        </button>
      </div>
      <div className="panel-b">
        {error !== null && <AvisoError error={error} />}
        <label className="campo">
          <span>
            Sitio <b>*</b>
          </span>
          <select value={sitio} onChange={(ev) => setSitio(ev.target.value)}>
            <option value="">Elige…</option>
            {sitios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.etiqueta}
              </option>
            ))}
          </select>
          <small className="muted">
            Solo sitios del mismo cliente que el equipo: el servidor rechaza los demas.
          </small>
        </label>
        <label className="campo">
          <span>Area destino</span>
          <input
            value={area}
            onChange={(ev) => setArea(ev.target.value)}
            placeholder="Caja 3, piso de venta, oficina…"
          />
          <small className="muted">Va impresa en la etiqueta de activo fijo.</small>
        </label>
      </div>
      <div className="panel-f">
        <button type="button" className="btn ghost" onClick={alCerrar}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn pri"
          disabled={sitio === '' || guardando}
          onClick={() => void guardar()}
          data-prueba="guardar-sitio"
        >
          {guardando ? 'Guardando…' : 'Asignar'}
        </button>
      </div>
    </div>
  );
}
